import React from 'react';

/**
 * The last line of defence: a crash while rendering.
 *
 * React unmounts the whole tree when a render throws and nothing catches it.
 * In development that is hidden behind the dev server's red overlay; in the
 * built app it is a white page with nothing on it and no way back — the same
 * "it just disappeared" that a failed file load used to produce, arriving by a
 * different route. A deck holding one damaged citation was enough to trigger
 * it.
 *
 * So a crash now lands here instead: it says what broke, it says the work is
 * still in the browser's recovery copy, and it offers a way onwards.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.area] - What was on screen, for the message
 */
export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: '' };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept for the details panel, and logged so a report can quote it.
    this.setState({ componentStack: info?.componentStack || '' });
    console.error('[AppErrorBoundary] Render failed', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleDismiss = () => {
    // Try the tree again. A crash caused by one damaged block usually returns,
    // but a transient one (a resize mid-render, a stale lazy chunk) does not,
    // and this saves a reload that would cost the unsaved edits.
    this.setState({ error: null, componentStack: '' });
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const area = this.props.area || 'the editor';

    return (
      <div className="min-h-screen flex items-start justify-center bg-gray-950/95 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
          <div className="border-b border-gray-200 bg-red-50 px-6 py-5 rounded-t-2xl">
            <h1 className="text-xl font-semibold text-gray-900">Something in {area} stopped working</h1>
            <p className="mt-1 text-sm text-gray-600">
              This is a fault in the app, not something you did.
            </p>
          </div>

          <div className="space-y-5 px-6 py-5 text-sm leading-relaxed text-gray-700">
            <p>
              The screen was cleared to stop the fault spreading. Your work is not gone: this
              app keeps a recovery copy in your browser, and reloading offers it back.
            </p>

            <div>
              <h2 className="text-sm font-semibold text-gray-900">What to try</h2>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li><strong>Try again</strong> first — a one-off fault does not come back.</li>
                <li>If it returns straight away, <strong>reload</strong> and accept the recovery copy.</li>
                <li>
                  If it keeps happening on the same deck, the deck holds something this version
                  cannot render. Open your last saved copy instead, and send the details below
                  with a bug report.
                </li>
              </ul>
            </div>

            <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <summary className="cursor-pointer font-medium text-gray-700">Technical details</summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs text-gray-600">
                <code>{`${error.message || String(error)}\n${componentStack}`.trim()}</code>
              </pre>
            </details>
          </div>

          <div className="flex flex-col gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
            >
              Reload the app
            </button>
            <button
              type="button"
              onClick={this.handleDismiss}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
}
